<div class="company-cases-page container-fluid">
  <div class="company-cases-shell d-flex flex-column mx-auto position-relative">
    <blocking-loader-overlay
      :show="page_loading"
      :message="page_loading_message"
      :detail="page_loading_detail"
    ></blocking-loader-overlay>
    <section class="company-cases-header">
      <div class="company-cases-hero">
        <div class="row g-2 align-items-start">
          <div class="col">
            <div class="company-cases-breadcrumb text-uppercase d-flex align-items-center gap-2">
              <span>Intake</span>
              <span>/</span>
              <span>Registered</span>
            </div>
            <h1 class="company-cases-title">Registered cases</h1>
            <p class="company-cases-intro mb-0">
              Registered work now lives on the shared case database. Search, filter and bulk-manage what has already
              passed intake and is waiting for triage, assignment or closure.
            </p>
          </div>
        </div>
      </div>

      <section class="row g-3 company-cases-metrics">
        <div v-for="metric in metrics" :key="metric.label" class="col-12 col-sm-6 col-xl-3">
          <article class="company-cases-metric h-100 shadow">
            <div class="company-cases-metric-label text-uppercase">{{ metric.label }}</div>
            <div class="company-cases-metric-value">{{ metric.value }}</div>
            <div class="company-cases-metric-change" :class="{ 'text-success': metric.meta_class === 'is-positive' }">
              {{ metric.meta }}
            </div>
          </article>
        </div>
      </section>
    </section>

    <section class="company-cases-list">
      <div class="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3 mb-3">
        <h2 class="company-cases-section-title mb-0">All cases</h2>

        <div class="d-flex align-items-center gap-2 flex-wrap">
          <button type="button" class="company-cases-new-btn">Export CSV</button>
          <button type="button" class="company-cases-new-btn" @click="open_new_case_modal">+ New case</button>
        </div>
      </div>

      <div class="row g-3 align-items-center mb-3">
        <div class="col-12 col-lg-4">
          <input
            v-model.trim="search_term"
            type="text"
            class="form-control company-case-modal-control shadow-sm"
            placeholder="Search cases by ID, name or owner"
          >
        </div>
        <div class="col-12 col-lg-8">
          <div class="company-cases-filters d-flex flex-wrap gap-2 mb-0">
            <button
              v-for="filter in filters"
              :key="filter.key"
              type="button"
              class="company-cases-filter shadow-sm"
              :class="{ 'is-active': active_filter === filter.key }"
              @click="set_filter(filter.key)"
            >
              {{ filter.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="d-flex flex-column flex-lg-row align-items-lg-center gap-3 border rounded shadow-sm bg-white p-3 mb-3">
        <label class="d-inline-flex align-items-center gap-2 mb-0">
          <input
            type="checkbox"
            class="form-check-input mt-0"
            :checked="all_visible_selected"
            @change="toggle_select_all($event)"
          >
          <span>Select all</span>
        </label>
        <span class="small text-secondary">{{ selected_case_ids.length }} selected</span>

        <div class="ms-lg-auto d-flex align-items-center gap-2">
          <button
            type="button"
            class="company-cases-new-btn"
            :disabled="closing_cases"
            @click="open_assign_modal"
          >
            Assign
          </button>
          <button
            type="button"
            class="company-cases-new-btn"
            :disabled="closing_cases"
            @click="open_close_modal"
          >
            {{ closing_cases ? 'Closing...' : 'Close' }}
          </button>
        </div>
      </div>

      <div class="company-cases-table-wrap shadow">
        <div class="table-responsive">
          <table class="table company-cases-table align-middle mb-0">
            <thead>
              <tr>
                <th class="w-auto"></th>
                <th>Case</th>
                <th class="text-nowrap">Domain</th>
                <th class="text-nowrap">Status</th>
                <th class="text-nowrap">Risk</th>
                <th class="text-nowrap">Owner</th>
                <th class="text-nowrap">SLA</th>
                <th class="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in filtered_cases"
                :key="item.case_code"
                class="case-clickable-row"
                @click="go_to_case_detail(item)"
              >
                <td @click.stop>
                  <input
                    :checked="selected_case_ids.includes(item.case_code)"
                    type="checkbox"
                    class="form-check-input mt-0"
                    @change="toggle_case(item.case_code)"
                  >
                </td>
                <td>
                  <div class="company-case-code text-uppercase">{{ item.case_code }}</div>
                  <div class="company-case-title">{{ item.title }}</div>
                  <div class="company-case-subtitle">{{ item.subtitle }}</div>
                </td>
                <td>
                  <span class="company-case-domain" :class="item.domain_badge_class">{{ item.domain }}</span>
                </td>
                <td>
                  <div class="company-case-status d-flex align-items-center gap-2">
                    <span class="status-dot-sm rounded-circle d-inline-block" :class="item.status_color ? '' : item.status_dot_class" :style="item.status_dot_style"></span>
                    <span>{{ item.status }}</span>
                  </div>
                </td>
                <td>
                  <span class="company-case-risk" :class="item.risk_badge_class">
                    {{ item.risk_score }} <span class="company-case-risk-separator">Â·</span> {{ item.risk_label }}
                  </span>
                </td>
                <td>
                  <div class="company-case-owner">{{ item.owner }}</div>
                </td>
                <td>
                  <span class="company-case-sla" :class="item.sla_class">
                    <i v-if="item.sla_icon" class="bi" :class="item.sla_icon"></i>
                    <span class="company-case-sla-text">{{ item.sla }}</span>
                  </span>
                </td>
                <td class="text-end" @click.stop>
                  <button
                    type="button"
                    class="company-case-view-btn"
                    @click.stop="go_to_case_detail(item)"
                  >
                    View
                  </button>
                </td>
              </tr>
              <tr v-if="cases_error">
                <td colspan="8" class="py-4 text-center text-secondary">{{ cases_error }}</td>
              </tr>
              <tr v-else-if="!loading_cases && !filtered_cases.length">
                <td colspan="8" class="py-4 text-center text-secondary">No registered cases match this filter.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>

  <div v-if="show_new_case_modal" class="company-case-modal-backdrop d-flex align-items-center justify-content-center" @click.self="close_new_case_modal">
    <section class="company-case-modal bg-white manual-intake-modal position-relative">
      <blocking-loader-overlay
        :show="submitting_manual_case"
        message="Registering case..."
        detail="Please wait while the case and its requestor form are being saved."
      ></blocking-loader-overlay>
      <header class="company-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="company-case-modal-title mb-2">{{ manual_case.title }}</h2>
          <div class="company-case-modal-copy">{{ manual_case.subtitle }}</div>
        </div>
        <button type="button" class="company-case-modal-close btn border-0 p-0" aria-label="Close modal" @click="close_new_case_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="company-case-modal-body">
        <p v-if="manual_case.description" class="mb-4 company-case-modal-description">
          {{ manual_case.description }}
        </p>

        <div class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="manual_intake_requester">Requester<span class="manual-required-marker">*</span></label>
          <input
            id="manual_intake_requester"
            v-model.trim="manual_case.requester"
            type="text"
            class="form-control company-case-modal-control"
            placeholder="Full name"
            :disabled="submitting_manual_case"
          >
        </div>

        <div class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="manual_intake_contact">Contact</label>
          <input
            id="manual_intake_contact"
            v-model.trim="manual_case.contact"
            type="text"
            class="form-control company-case-modal-control"
            placeholder="Phone or email"
            :disabled="submitting_manual_case"
          >
        </div>

        <div class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="manual_intake_location">Location / Address</label>
          <input
            id="manual_intake_location"
            v-model.trim="manual_case.location"
            type="text"
            class="form-control company-case-modal-control"
            placeholder="Address or area"
            :disabled="submitting_manual_case"
          >
        </div>

        <div class="mb-0">
          <label class="company-case-modal-label text-uppercase mb-2" for="manual_intake_case_type">Case type<span class="manual-required-marker">*</span></label>
          <select
            id="manual_intake_case_type"
            v-model="manual_case.case_type"
            class="form-select company-case-modal-control"
            :disabled="submitting_manual_case"
          >
            <option value="" disabled>Select a case type</option>
            <option v-for="option in new_case_domain_options" :key="option.id" :value="String(option.id)">{{ option.label }}</option>
          </select>
        </div>
      </div>

      <footer class="company-case-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border px-4" :disabled="submitting_manual_case" @click="close_new_case_modal">
          Need more info
        </button>
        <button
          type="button"
          class="btn btn-primary-dark px-4"
          :disabled="submitting_manual_case || !can_create_new_case"
          @click="create_new_case"
        >
          {{ submitting_manual_case ? 'Registering...' : 'Register -> onto main DB' }}
        </button>
      </footer>
    </section>
  </div>

  <div v-if="show_assign_modal" class="company-case-modal-backdrop d-flex align-items-center justify-content-center" @click.self="close_assign_modal">
    <section class="company-case-modal bg-white position-relative">
      <header class="company-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="company-case-modal-title mb-2">Assign selected cases</h2>
          <div class="company-case-modal-copy">
            Assign {{ selected_case_ids.length }} selected case{{ selected_case_ids.length === 1 ? '' : 's' }} to a panel case handler.
          </div>
        </div>
        <button type="button" class="company-case-modal-close btn border-0 p-0" aria-label="Close assign modal" @click="close_assign_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="company-case-modal-body">
        <div v-if="assign_error" class="small text-danger fw-semibold mb-3">
          {{ assign_error }}
        </div>

        <div v-if="loading_assignable_users" class="small text-secondary">
          Loading panel case handlers...
        </div>

        <div v-else-if="assignable_users.length" class="d-flex flex-column gap-2">
          <label
            v-for="user in assignable_users"
            :key="user.user_id"
            class="case-details-decision-option company-case-route-option"
            :class="{ 'is-selected': String(selected_assign_user_id) === String(user.user_id) }"
          >
            <input
              v-model="selected_assign_user_id"
              class="case-details-decision-radio"
              type="radio"
              :value="String(user.user_id)"
              :disabled="submitting_assign_cases"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span>
              <span class="case-details-decision-option-title">{{ user.name }}</span>
              <span class="case-details-decision-option-body">{{ user.role }}</span>
            </span>
          </label>
        </div>

        <div v-else class="small text-secondary">
          No panel case handlers are available for assignment.
        </div>
      </div>

      <footer class="company-case-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border px-4" :disabled="submitting_assign_cases" @click="close_assign_modal">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary-dark px-4"
          :disabled="!can_submit_assign"
          @click="assign_selected_cases"
        >
          {{ submitting_assign_cases ? 'Assigning...' : 'Assign case(s)' }}
        </button>
      </footer>
    </section>
  </div>

  <div v-if="show_close_modal" class="company-case-modal-backdrop d-flex align-items-center justify-content-center" @click.self="close_close_modal">
    <section class="company-case-modal bg-white position-relative">
      <header class="company-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="company-case-modal-title mb-2">Close selected cases</h2>
          <div class="company-case-modal-copy">
            Move {{ selected_case_ids.length }} selected case{{ selected_case_ids.length === 1 ? '' : 's' }} to the final workflow status and mark {{ selected_case_ids.length === 1 ? 'it' : 'them' }} closed.
          </div>
        </div>
        <button type="button" class="company-case-modal-close btn border-0 p-0" aria-label="Close close modal" @click="close_close_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="company-case-modal-body">
        <div v-if="close_error" class="small text-danger fw-semibold mb-3">
          {{ close_error }}
        </div>

        <div class="small text-secondary">
          This action uses the configured final workflow status for each selected case.
        </div>
      </div>

      <footer class="company-case-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border px-4" :disabled="closing_cases" @click="close_close_modal">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary-dark px-4"
          :disabled="closing_cases || !selected_case_ids.length"
          @click="close_selected_cases"
        >
          {{ closing_cases ? 'Closing...' : 'Close case(s)' }}
        </button>
      </footer>
    </section>
  </div>
</div>
